class ArticleTocEl extends HTMLElement {
  static get observedAttributes() {
    return ["fixed-top", "pass-delta", "scroll-offset", "is-full-width"];
  }

  connectedCallback() {
    this.fixedTop = this._numAttr("fixed-top", 104);
    this.passDelta = this._numAttr("pass-delta", 100);
    this.scrollOffset = this._numAttr("scroll-offset", this.fixedTop + 8);
    this.isFullWidth = this._boolAttr("is-full-width");
    this.extraScrollPad = 50;

    this._shellEl = document.querySelector("[data-header-shell]");

    this.bar = this.querySelector(".toc__bar");
    this.panel = this.querySelector(".toc__panel");
    this.list = this.querySelector(".toc__list");
    if (this.panel) this.panel.style.transitionDuration = "200ms";

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onBarClickRel = this._onBarClickRel.bind(this);
    this._onBarClickClone = this._onBarClickClone.bind(this);
    this._onListClick = this._onListClick.bind(this);
    this._onMQChange = this._onMQChange.bind(this);

    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onResize);
    this.bar?.addEventListener("click", this._onBarClickRel);
    this.list?.addEventListener("click", this._onListClick);
    this._cloneList?.addEventListener("click", this._onListClick);

    this.isOpenRel = true;
    this._applyOpenRel();

    this._measureHeader();
    this._insertStartSentinel();
    this._ensureClone();
    this._setupShowMore();
    this._setupMQ();

    this._ro = new ResizeObserver(() => {
      this._applyLayoutStyles();
      this._onScroll();
    });
    this._ro.observe(this);
    if (this._shellEl) this._ro.observe(this._shellEl);
    const article = document.getElementById("articleContent");
    if (article) this._ro.observe(article);

    this._applyLayoutStyles();
    this._onScroll();

    // Track header hide/show (mobile/tablet) to reposition TOC clone
    this._headerHidden = document.body.classList.contains("js-header-hidden");
    this._bodyObs = new MutationObserver(() => {
      const hidden = document.body.classList.contains("js-header-hidden");
      if (hidden !== this._headerHidden) {
        this._headerHidden = hidden;
        this._syncCloneTop();
      }
    });
    this._bodyObs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  _setupShowMore() {
    const MAX_VISIBLE = 10;

    const list = this.querySelector(".toc__list");
    if (!list) return;

    const items = Array.from(list.querySelectorAll(".toc__item"));
    if (!items.length || items.length <= MAX_VISIBLE) return;

    items.forEach((item, index) => {
      if (index >= MAX_VISIBLE) {
        item.classList.add("toc__item--hidden");
      }
    });

    const btn = this.querySelector(".toc__more-btn");
    const overlay = this.querySelector(".toc__more-overlay");
    if (!btn) return;

    const onClick = (e) => {
      e.preventDefault();

      items.forEach((item) => item.classList.remove("toc__item--hidden"));

      btn.style.display = "none";
      if (overlay) overlay.style.display = "none";
    };

    btn.addEventListener("click", onClick);
    this._moreBtn = btn;
    this._moreBtnHandler = onClick;
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onResize);
    this.bar?.removeEventListener("click", this._onBarClickRel);
    this.list?.removeEventListener("click", this._onListClick);
    this._cloneBar?.removeEventListener("click", this._onBarClickClone);
    this._cloneList?.removeEventListener("click", this._onListClick);

    if (this._moreBtn && this._moreBtnHandler) {
      this._moreBtn.removeEventListener("click", this._moreBtnHandler);
    }

    if (this._ro) this._ro.disconnect();
    if (this._bodyObs) this._bodyObs.disconnect();
    this._teardownMQ();
  }

  attributeChangedCallback(name) {
    if (name === "is-full-width")
      this.isFullWidth = this._boolAttr("is-full-width");
    this.fixedTop = this._numAttr("fixed-top", 104);
    this.passDelta = this._numAttr("pass-delta", 100);
    this.scrollOffset = this._numAttr("scroll-offset", this.fixedTop + 8);
    if (this._clone)
      this._clone.style.top = `${-(this._headerH || 52)}px`;
    this._measureHeader();
    this._applyLayoutStyles();
    this._onScroll();
  }

  _numAttr(name, fallback) {
    const v = parseInt(this.getAttribute(name) || "", 10);
    return Number.isFinite(v) ? v : fallback;
  }

  _boolAttr(name) {
    const v = (this.getAttribute(name) || "").toLowerCase().trim();
    return v === "true" || v === "1" || v === "yes";
  }

  _y(el) {
    const r = el.getBoundingClientRect();
    return r.top + window.scrollY;
  }

  _measureHeader() {
    const h = this.bar ? this.bar.offsetHeight : 0;
    this._headerH = h > 0 ? h : 52;
  }

  _insertStartSentinel() {
    if (this._startSentinel) return;
    const s = document.createElement("div");
    s.style.cssText = "height:0;visibility:hidden;";
    this.after(s);
    this._startSentinel = s;
  }

  _applyOpenRel() {
    this.classList.toggle("active", this.isOpenRel);
    this.bar?.setAttribute("aria-expanded", this.isOpenRel ? "true" : "false");
  }

  _applyOpenClone() {
    if (!this._clone) return;

    this._clone.classList.toggle("active", this.isOpenClone);
    this._cloneBar?.setAttribute(
      "aria-expanded",
      this.isOpenClone ? "true" : "false"
    );

    if (!this._clonePanel) return;

    if (this.isOpenClone) {
      document.body.style.overflow = "hidden";

      const maxPanelHeight = Math.max(
        window.innerHeight - this._siteHeaderHeight - (this._headerH || 52),
        120
      );
      this._clonePanel.style.maxHeight = `${maxPanelHeight}px`;
      this._clonePanel.style.overflowY = "auto";
    } else {
      document.body.style.overflow = "";
      this._clonePanel.style.maxHeight = "0px";
      this._clonePanel.style.overflowY = "hidden";
    }
  }

  _onBarClickRel() {
    this.isOpenRel = !this.isOpenRel;
    this._applyOpenRel();
  }

  _onBarClickClone() {
    this.isOpenClone = !this.isOpenClone;
    this._applyOpenClone();
  }

  _findTargetForLink(link) {
    const raw = link.getAttribute("href") || "";
    const hashIndex = raw.indexOf("#");
    let target = null;

    if (hashIndex !== -1) {
      let id = raw.slice(hashIndex + 1);
      if (id) {
        try {
          id = decodeURIComponent(id);
        } catch (err) {}

        target = document.getElementById(id);
        if (target) return target;

        const normalizedId = id
          .replace(/[^a-zA-Z0-9\-_:.]/g, "-")
          .replace(/-+/g, "-")
          .replace(/^-|-$/g, "");

        if (normalizedId && normalizedId !== id) {
          target = document.getElementById(normalizedId);
          if (target) return target;
        }
      }
    }

    const article = document.getElementById("articleContent");
    if (article) {
      const linkText = (link.textContent || "").trim();
      if (linkText) {
        const headings = article.querySelectorAll("h1,h2,h3,h4,h5,h6");
        for (const h of headings) {
          if ((h.textContent || "").trim() === linkText) {
            return h;
          }
        }
      }
    }

    return null;
  }

  _onListClick(e) {
    const link = e.target.closest("a[href]");
    if (!link) return;

    e.preventDefault();
    e.stopPropagation();

    const target = this._findTargetForLink(link);
    if (!target) return;

    const scrollY =
      target.getBoundingClientRect().top +
      window.scrollY -
      (this._siteHeaderHeight + 8 + this.extraScrollPad);

    window.scrollTo({ top: scrollY, behavior: "smooth" });

    if (e.currentTarget === this._cloneList) {
      this.isOpenClone = false;
      this._applyOpenClone();
    }
  }

  _prepareCloneForFullList() {
    if (!this._clone) return;

    const hiddenItems = this._clone.querySelectorAll(".toc__item--hidden");
    hiddenItems.forEach((item) => {
      item.classList.remove("toc__item--hidden");
    });

    const moreWrap = this._clone.querySelector(".toc__more-btn-wrap");
    if (moreWrap && moreWrap.parentNode) {
      moreWrap.parentNode.removeChild(moreWrap);
    }

    const overlay = this._clone.querySelector(".toc__more-overlay");
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }

  _ensureClone() {
    if (this._clone) return;

    const wrapper = document.createElement("div");
    wrapper.className =
      "toc fixed left-0 right-0 z-1 opacity-0 pointer-events-none";
    wrapper.style.transition =
      "opacity 200ms linear, top 300ms cubic-bezier(0.4, 0, 0.2, 1)";
    wrapper.style.top = `${-(this._headerH || 52)}px`;

    wrapper.innerHTML = `
      <div class="px-2 lg:px-4" style="margin:0 auto;">
        ${this.innerHTML}
      </div>
    `;

    document.body.appendChild(wrapper);

    this._clone = wrapper;
    this._cloneInner = wrapper.firstElementChild;
    this._cloneBar = wrapper.querySelector(".toc__bar");
    this._cloneList = wrapper.querySelector(".toc__list");
    this._clonePanel = wrapper.querySelector(".toc__panel");

    const cloneMoreWrap = wrapper.querySelector(".toc__more-btn-wrap");
    if (cloneMoreWrap) cloneMoreWrap.remove();

    const cloneFade = wrapper.querySelector(".toc__more-overlay");
    if (cloneFade) cloneFade.remove();

    if (this._cloneList) {
      this._cloneList.querySelectorAll("li").forEach((li) => {
        li.style.display = "block";
      });
    }

    if (this._clonePanel) {
      this._clonePanel.style.transition = "none";
      this._clonePanel.style.maxHeight = "none";
      this._clonePanel.style.overflow = "visible";
    }

    const vh = window.innerHeight || 0;
    const h = vh - this._siteHeaderHeight;
    this._cloneInner.style.height = h + "px";
    this._cloneInner.style.maxHeight = h + "px";
    this._cloneInner.style.overflowY = "auto";

    this._cloneBar?.addEventListener("click", this._onBarClickClone);
    this._cloneList?.addEventListener("click", this._onListClick);

    this.isOpenClone = false;
    this._applyOpenClone();
  }

  _applyLayoutStyles() {
    if (!this._cloneInner) return;

    if (this.isFullWidth) {
      this._cloneInner.style.maxWidth = "1032px";
      this._cloneInner.style.width = "100%";
      this._cloneInner.style.margin = "0 auto";
      this._cloneInner.style.position = "";
      this._cloneInner.style.left = "";
    } else {
      const vw = window.innerWidth || 0;
      if (vw < 1220) {
        this._cloneInner.style.maxWidth = "1252px";
        this._cloneInner.style.width = "100%";
        this._cloneInner.style.margin = "0 auto";
        this._cloneInner.style.position = "";
        this._cloneInner.style.left = "";
      } else {
        const leftColWidth = this.getBoundingClientRect().width || 0;
        if (leftColWidth > 0) {
          this._cloneInner.style.maxWidth = "none";
          this._cloneInner.style.width = `${leftColWidth + 32}px`;
        } else {
          this._cloneInner.style.maxWidth = "none";
          this._cloneInner.style.width = "71.4286%";
        }
        if (vw >= 1920) {
          this._cloneInner.style.margin = "0";
          this._cloneInner.style.position = "relative";
          this._cloneInner.style.left = `${(window.innerWidth - 1872) / 2}px`;
        } else {
          this._cloneInner.style.margin = "0 24px";
          this._cloneInner.style.position = "";
          this._cloneInner.style.left = "";
        }
      }
    }

    const vh = window.innerHeight || 0;
    const h = vh - this._siteHeaderHeight;
    if (h > 0) {
      this._cloneInner.style.height = h + "px";
      this._cloneInner.style.maxHeight = h + "px";
    } else {
      this._cloneInner.style.height = "";
      this._cloneInner.style.maxHeight = "";
    }
    this._cloneInner.style.overflowY = "auto";
  }

  _setupMQ() {
    this._mql1220 = window.matchMedia("(min-width: 1220px)");
    this._mql1920 = window.matchMedia("(min-width: 1920px)");
    this._mql1220.addEventListener("change", this._onMQChange);
    this._mql1920.addEventListener("change", this._onMQChange);
  }

  _teardownMQ() {
    if (this._mql1220)
      this._mql1220.removeEventListener("change", this._onMQChange);
    if (this._mql1920)
      this._mql1920.removeEventListener("change", this._onMQChange);
  }

  _onMQChange() {
    this._applyLayoutStyles();
    this._onScroll();
  }

  get _siteHeaderHeight() {
    if (!this._shellEl) return this.fixedTop;
    const bottom = this._shellEl.getBoundingClientRect().bottom;
    return bottom > 0 ? Math.round(bottom) : 0;
  }

  get _shownTop() {
    if (this._headerHidden && window.innerWidth < 1024) return 0;
    return this._siteHeaderHeight;
  }

  _showClone() {
    if (!this._clone) return;
    this._cloneVisible = true;
    this._clone.style.top = `${this._shownTop}px`;
    this._clone.classList.remove("opacity-0");
    this._clone.classList.add("opacity-100");
  }

  _hideClone() {
    if (!this._clone) return;

    if (this.isOpenClone) {
      this.isOpenClone = false;
      this._applyOpenClone();
    }

    this._cloneVisible = false;
    this._clone.style.top = `${-(this._headerH || 52)}px`;
    this._clone.classList.add("opacity-0");
    this._clone.classList.remove("opacity-100");
  }

  _syncCloneTop() {
    if (!this._clone || !this._cloneVisible) return;
    this._clone.style.top = `${this._shownTop}px`;
  }

  _onScroll() {
    const y = window.scrollY;
    const startY = this._startSentinel
      ? this._y(this._startSentinel)
      : this._y(this) + (this.offsetHeight || 0);
    const article = document.getElementById("articleContent");
    const endY = article
      ? this._y(article) + article.offsetHeight - window.innerHeight
      : Infinity;
    const show = y >= startY && y < endY;
    if (show) this._showClone();
    else this._hideClone();
  }

  _onResize() {
    this._measureHeader();
    this._applyLayoutStyles();

    if (this._clone) {
      if (this._cloneVisible) {
        this._clone.style.top = `${this._shownTop}px`;
      } else {
        this._clone.style.top = `${-(this._headerH || 52)}px`;
      }
    }

    if (this.isOpenClone && this._clonePanel) {
      const maxPanelHeight = Math.max(
        window.innerHeight - this._siteHeaderHeight - (this._headerH || 52),
        120
      );
      this._clonePanel.style.maxHeight = `${maxPanelHeight}px`;
      this._clonePanel.style.overflowY = "auto";
    }

    this._onScroll();
  }
}

customElements.define("article-toc", ArticleTocEl);
